"use client";

import { getMoneyFormatLocale } from "@/i18n/types";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { useMemo } from "react";

import {
  findCategoryLabel,
  formatPriceLabel,
} from "../helpers/filtersChips.helper";
import type { ChipItem } from "../helpers/filtersChips.helper";
import type {
  CustomCategoryFacet,
  CustomColorFacet,
  CustomPriceFacet,
  DefaultListFacet,
  Facet,
} from "../types/filters.type";
import { useFilterParams } from "./useFilterParams.hook";
import { useFiltersQuery } from "./useFiltersQuery.hook";

function isPathBound(field: string, pathname: string): boolean {
  if (field === "brand") return pathname.startsWith("/b/");
  return false;
}

function isListFacet(
  facet: Facet,
): facet is DefaultListFacet | CustomColorFacet {
  return (
    (facet.component === "Default" && facet.selector !== "Boolean") ||
    facet.component === "CustomColor"
  );
}

function resolveListChips(
  facet: DefaultListFacet | CustomColorFacet,
): ChipItem[] {
  return facet.values
    .filter((v) => v.is_selected)
    .map((v) => ({ field: facet.field, value: v.value, label: v.label }));
}

function resolvePriceChip(
  facet: CustomPriceFacet,
  priceMin: number | null,
  priceMax: number | null,
  locale: string,
): ChipItem | null {
  if (priceMin == null || priceMax == null) return null;
  return {
    field: facet.field,
    value: `${priceMin},${priceMax}`,
    label: formatPriceLabel(priceMin, priceMax, facet.currency, locale),
  };
}

function resolveCategoryChips(
  facet: CustomCategoryFacet,
  categoriesIds: string[] | null,
): ChipItem[] {
  if (!categoriesIds?.length) return [];
  return categoriesIds.map((id) => ({
    field: "categories_ids",
    value: id,
    label: findCategoryLabel(facet.tree, id) ?? id,
  }));
}

type ResolveContext = {
  pathname: string;
  priceMin: number | null;
  priceMax: number | null;
  categoriesIds: string[] | null;
  locale: string;
};

function resolveChipsForFacet(facet: Facet, ctx: ResolveContext): ChipItem[] {
  if (isPathBound(facet.field, ctx.pathname)) return [];
  if (isListFacet(facet)) return resolveListChips(facet);
  if (facet.component === "CustomPrice") {
    const chip = resolvePriceChip(
      facet,
      ctx.priceMin,
      ctx.priceMax,
      ctx.locale,
    );
    return chip ? [chip] : [];
  }
  if (facet.component === "CustomCategory") {
    return resolveCategoryChips(facet, ctx.categoriesIds);
  }
  return [];
}

export function useAppliedFilteredItems(): ChipItem[] {
  const pathname = usePathname();
  const { facets } = useFiltersQuery();
  const [filterParams] = useFilterParams();
  const moneyLocale = getMoneyFormatLocale(useLocale());

  const priceMin = filterParams.price_min;
  const priceMax = filterParams.price_max;
  const categoriesIds = filterParams.categories_ids;

  return useMemo(
    () =>
      facets.flatMap((facet) =>
        resolveChipsForFacet(facet, {
          pathname,
          priceMin,
          priceMax,
          categoriesIds,
          locale: moneyLocale,
        }),
      ),
    [facets, pathname, priceMin, priceMax, categoriesIds, moneyLocale],
  );
}
